import { z } from 'zod';
import {
  DEVICE_TYPE_CODE_BY_NAME,
  DeviceTypeName,
  PLANT_MEASUREMENT_CODE_BY_NAME,
  PLANT_MEASUREMENT_NAME_BY_CODE,
  PlantMeasurementCodes,
} from '@/features/devices/schemas/devices.schemas.ts';
import {
  DEVICE_FIELDS_METADATA,
  getDeviceFieldByFieldId,
  PLANT_FIELDS_METADATA,
} from '@/features/devices/schemas/devices.metadata.ts';

export type RuleType = 'plant' | 'device' | 'schedule';

export type ConditionFormFields = {
  ruleType: RuleType;
  deviceType: DeviceTypeName | undefined;
  conditions: Array<{ fact: string; operator: string; value: string }>;
};

export const conditionSchema = z.object({
  fact: z.string().min(1, 'Parameter is required'),
  operator: z.string().min(1, 'Operator is required'),
  value: z.string().min(1, 'Value is required'),
});

export const OPERATORS = [
  { value: 'equal', label: 'Equal to' },
  { value: 'notEqual', label: 'Not equal to' },
  { value: 'lessThan', label: 'Less than' },
  { value: 'lessThanInclusive', label: 'Less than or equal to' },
  { value: 'greaterThan', label: 'Greater than' },
  { value: 'greaterThanInclusive', label: 'Greater than or equal to' },
];

export const SCHEDULE_FACT_LABELS: Record<string, string> = {
  'schedule.day_without_setpoints': 'No active setpoints for a day',
  'schedule.charging_window_active': 'Charging window active',
  'schedule.discharging_window_active': 'Discharging window active',
};

export const getAvailableParameters = ({
  watchRuleType,
  watchDeviceType,
}: {
  watchRuleType: RuleType;
  watchDeviceType: DeviceTypeName | undefined;
}) => {
  if (watchRuleType === 'schedule') {
    return Object.entries(SCHEDULE_FACT_LABELS).map(([id, name]) => ({
      id,
      name,
      unit: undefined,
    }));
  }

  if (watchRuleType === 'device' && watchDeviceType) {
    const deviceTypeId = DEVICE_TYPE_CODE_BY_NAME[watchDeviceType as DeviceTypeName];
    const deviceMetadata = DEVICE_FIELDS_METADATA[deviceTypeId];

    if (deviceMetadata) {
      return Object.values(deviceMetadata.parametersByCode)
        .map((param) => ({
          id: param?.id,
          name: param?.name,
          unit: param?.unit,
        }))
        .sort((a, b) => a?.name?.localeCompare(b?.name));
    }
  }

  return Object.entries(PLANT_MEASUREMENT_CODE_BY_NAME)
    .map(([name, id]) => ({
      id: id,
      name: name,
      unit: undefined,
    }))
    .sort((a, b) => a?.name?.localeCompare(b?.name));
};

export function getParameterLabel({
  watchRuleType,
  watchDeviceType,
  fieldValue,
}: {
  watchRuleType: RuleType;
  watchDeviceType: DeviceTypeName | undefined;
  fieldValue: number | string;
}) {
  if (watchRuleType === 'schedule') {
    return SCHEDULE_FACT_LABELS[String(fieldValue)] ?? 'Unknown condition';
  }

  if (watchRuleType === 'device' && watchDeviceType) {
    const field = getDeviceFieldByFieldId(watchDeviceType, Number(fieldValue));

    return field?.name;
  }

  return PLANT_MEASUREMENT_NAME_BY_CODE[fieldValue as PlantMeasurementCodes];
}

export function getDeviceParameterInfo(
  deviceType: DeviceTypeName | undefined,
  condition: {
    fact: string;
    operator: string;
    value: string;
  },
  index: number
) {
  if (!deviceType) return null;

  const paramName = getDeviceFieldByFieldId(deviceType, +condition.fact)?.name;
  if (!paramName) return null;

  return {
    fact: `{{condition${index + 1}Parameter}}`,
    value: `{{condition${index + 1}Value}}`,
    threshold: `{{condition${index + 1}Threshold}}`,
    paramName,
    thresholdValue: undefined,
  };
}

export function getPlantParameterInfo(
  condition: {
    fact: string;
    operator: string;
    value: string;
  },
  index: number
) {
  const mqttStatusCode = PLANT_MEASUREMENT_CODE_BY_NAME['MQTT Connection Status'];
  if (Number(condition.fact) !== mqttStatusCode) return null;

  const paramName = PLANT_MEASUREMENT_NAME_BY_CODE[mqttStatusCode];
  if (!paramName) return null;

  const options = Object.values(PLANT_FIELDS_METADATA[mqttStatusCode].parametersByCode);
  const thresholdValue = options?.find(({ id }) => id === Number(condition.value))?.name;

  return {
    fact: `{{condition${index + 1}Parameter}}`,
    value: `{{condition${index + 1}Value}}`,
    threshold: `{{condition${index + 1}Threshold}}`,
    paramName,
    thresholdValue: thresholdValue || undefined,
  };
}
